import BillingsTaxiDriverUpdateParams, {
  BillingsTaxiDriverUpdateParamsContribution,
  BillingsTaxiDriverUpdateParamsContributionBonus,
  BillingsTaxiDriverUpdateParamsContributionContractCorrection,
  BillingsTaxiDriverUpdateParamsContributionPenalty,
  BillingsTaxiDriverUpdateParamsContributionType,
  BillingsTaxiDriverUpdateParamsNode,
} from "../../../../../common/services/billings/taxi-driver/update/billings-taxi-driver-update-params";
import {
  BillingsTaxiDriverContributionBonusReal,
  BillingsTaxiDriverContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-driver-contributions-bonus";
import {
  BillingsTaxiDriverContributionContractCorrectionReal,
  BillingsTaxiDriverContributionContractCorrectionType,
} from "../../common/contributions/contract-correction/types/billings-taxi-driver-contributions-contract-correction";
import {
  BillingsTaxiDriverContributionPenaltyReal,
  BillingsTaxiDriverContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-driver-contributions-penalty";
import BillingFormData, {
  BillingFormDataContributions,
  BillingSummaryData,
} from "../types/billing-form.data";

const createBillingBonusContribution = (
  contribution: BillingsTaxiDriverContributionBonusReal,
): BillingsTaxiDriverUpdateParamsContributionBonus => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance ?? 0,
        rate: contribution.rate ?? 0,
      };
    case BillingsTaxiDriverContributionBonusType.BONUS_OTHER:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiDriverContributionPenaltyReal,
): BillingsTaxiDriverUpdateParamsContributionPenalty => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_BEING_LATE:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.PENALTY_BEING_LATE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.PENALTY_INCOMPATIBLE_CAR,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_OTHER:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.PENALTY_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionPenaltyType.PENALTY_WRONG_ROUTE:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.PENALTY_WRONG_ROUTE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingContractCorrectionContribution = (
  contribution: BillingsTaxiDriverContributionContractCorrectionReal,
  distance: number,
): BillingsTaxiDriverUpdateParamsContributionContractCorrection => {
  switch (contribution.type) {
    case BillingsTaxiDriverContributionContractCorrectionType.BASE_AMOUNT_EXTERNAL_TAXI:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.BASE_AMOUNT_EXTERNAL_TAXI,
        comment: "",
        distance: distance ?? 0,
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiDriverContributionContractCorrectionType.CONTRACT_CORRECTION_RATE:
      return {
        type: BillingsTaxiDriverUpdateParamsContributionType.CONTRACT_CORRECTION_RATE,
        comment: "",
        rate: contribution.rate ?? 0,
      };
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
  distance: number,
): BillingsTaxiDriverUpdateParamsContribution[] => {
  const bonusContributions = contributions.bonus.filter(
    (contribution) => contribution.type !== null,
  ) as BillingsTaxiDriverContributionBonusReal[];

  const penaltyContributions = contributions.penalty.filter(
    (contribution) => contribution.type !== null,
  ) as BillingsTaxiDriverContributionPenaltyReal[];

  const contractCorrectionContributions =
    contributions.contractCorrection.filter(
      (contribution) => contribution.type !== null,
    ) as BillingsTaxiDriverContributionContractCorrectionReal[];

  return [
    ...bonusContributions.map((contribution) =>
      createBillingBonusContribution(contribution),
    ),
    ...penaltyContributions.map((contribution) =>
      createBillingPenaltyContribution(contribution),
    ),
    ...contractCorrectionContributions.map((contribution) =>
      createBillingContractCorrectionContribution(contribution, distance),
    ),
  ];
};

const createUpdateParams = (
  billingUuid: string,
  data: BillingFormData,
  summary: BillingSummaryData,
): BillingsTaxiDriverUpdateParams => {
  return {
    billingUuid: billingUuid,
    billingNodes: createBillingNodes(data),
    billingContributions: createBillingContributions(
      data.billingContributions,
      summary.distance,
    ),
  };
};

const createBillingNodes = (
  data: BillingFormData,
): BillingsTaxiDriverUpdateParamsNode[] => {
  return data.billingNodes.map((specificNode) => {
    const node: BillingsTaxiDriverUpdateParamsNode = {
      uuid: specificNode.id,
      nodeUuid: specificNode.nodeId,
      orderUuid: specificNode.orderId,
      lat: specificNode.lat,
      lon: specificNode.lon,
      distance: specificNode.distance,
      description: specificNode.description,
      checkoutDate: specificNode.checkoutDate!,
      checkoutEventUuid: specificNode.checkoutEventUuid,
      plannedDate: specificNode.plannedDate!,
      haltingTime: specificNode.haltingTime,
      allowCharge: specificNode.isHighwayAllowed,
      highwayCharge: specificNode.highwayCharge,
      plannedDistance: specificNode.plannedDistance,
      editableCoordinates: specificNode.isEditable,
    };
    return node;
  });
};

const billingSaveRequestFactory = {
  createUpdateParams,
};

export default billingSaveRequestFactory;
